export type StoredWorkspaceSkillsSettings = {
    enabled: boolean;
    activatedAt: string;
};

const STORAGE_KEY_PREFIX = 'bustly:workspace-skills-settings:';

type ReadableStorage = Pick<Storage, 'getItem'>;
type WritableStorage = Pick<Storage, 'setItem'>;

const resolveBrowserStorage = (): Storage | null => {
    if (typeof window === 'undefined' || typeof window.localStorage === 'undefined') {
        return null;
    }
    return window.localStorage;
};

const buildStorageKey = (workspaceId: string) => `${STORAGE_KEY_PREFIX}${workspaceId}`;

export const readStoredWorkspaceSkillsSettings = (
    workspaceId: string,
    storage: ReadableStorage | null = resolveBrowserStorage(),
): StoredWorkspaceSkillsSettings | null => {
    if (!workspaceId || !storage) {
        return null;
    }

    try {
        const raw = storage.getItem(buildStorageKey(workspaceId));
        if (!raw) {
            return null;
        }
        const parsed = JSON.parse(raw) as Record<string, unknown>;
        const activatedAt = typeof parsed.activatedAt === 'string' ? parsed.activatedAt.trim() : '';
        if (typeof parsed.enabled !== 'boolean' || !activatedAt) {
            return null;
        }
        return {
            enabled: parsed.enabled,
            activatedAt,
        };
    } catch {
        return null;
    }
};

export const writeStoredWorkspaceSkillsSettings = (
    workspaceId: string,
    settings: StoredWorkspaceSkillsSettings,
    storage: WritableStorage | null = resolveBrowserStorage(),
): void => {
    if (!workspaceId || !storage) {
        return;
    }

    try {
        storage.setItem(buildStorageKey(workspaceId), JSON.stringify(settings));
    } catch {
        // Keep the in-memory state usable when an embedded host blocks storage access.
    }
};
